/**
 * Route path constants
 * Grouped by feature for better organization and maintainability
 */

// Base routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  NOT_FOUND: '*',
  FORBIDDEN: '/403',
} as const;

// Auth routes
export const AUTH_ROUTES = {
  CALLBACK: '/auth/callback',
  ERROR: '/auth/error',
  CLIO_REDIRECT: '/clio-redirect',
  QBO_REDIRECT: '/qbo-redirect',
} as const;

// Reports routes
export const REPORTS_ROUTES = {
  BASE: '/reports',
  AR: {
    BASE: '/reports/ar',
    DASHBOARD: '/reports/ar/dashboard',
    HISTORY: '/reports/ar/history',
  },
  TRUST: {
    BASE: '/reports/trust',
    DASHBOARD: '/reports/trust/dashboard',
    HISTORY: '/reports/trust/history',
  },
  MATTER_LEDGER: {
    BASE: '/reports/matter-ledger',
    DASHBOARD: '/reports/matter-ledger/dashboard',
    HISTORY: '/reports/matter-ledger/history',
  },
  MATTER_STATEMENT: {
    BASE: '/reports/matter-statement',
    DASHBOARD: '/reports/matter-statement/dashboard',
    HISTORY: '/reports/matter-statement/history',
  },
} as const;

// Reports routes
export const AR_COLLECTIONS_ROUTES = {
  BASE: '/ar-collections',
  AR: {
    COLLECTIONS: '/ar-collections/queue',
    SETTINGS: '/ar-collections/settings',
    REPORTING: '/ar-collections/reporting',
    EMAIL_QUEUE: '/ar-collections/email-queue',
    APPEALS: '/ar-collections/appeals',
    WRITE_OFFS: '/ar-collections/write-offs',
    PERFORMANCE: '/ar-collections/performance',
    LEDGER_REPORTS: '/ar-collections/ledger-reports',
    ESCALATION_REPORT: '/ar-collections/escalation-report',
  },
  PUBLIC: {
    APPEAL_DETAIL_ATTORNEY_RESPONSE:
      '/ar-collections/public/invoice/:invoiceId/appeal/:appealId/attorney-response',
  },
} as const;

// Settings routes
export const SETTINGS_ROUTES = {
  BASE: '/settings',
  REPORTING_CONFIG: '/settings/reporting-config',
  ARCHIVAL: '/settings/archival',
  JUNIPER_CONFIG: '/settings/juniper-config',
  TEMPLATE_MANAGER: '/settings/template-manager',
  WEBHOOKS: '/settings/webhooks',
  WEBHOOK_ACTIVITY: '/settings/webhook-activity',
  QBO_WEBHOOKS: '/settings/qbo-webhooks',
  QBO_WEBHOOK_ACTIVITY: '/settings/qbo-webhook-activity',
} as const;

// Juniper routes
export const JUNIPER_ROUTES = {
  TRANSACTIONS: '/juniper-transactions',
  PDF_VIEWER: '/juniper/pdf-viewer/:juniperId',
  DOCUMENT_VIEWER: '/juniper/document-viewer/:juniperId',
} as const;

// Trust Request routes
export const TRUST_REQUEST_ROUTES = {
  CREATE: '/trust-requests/create',
} as const;

// Support routes
export const SUPPORT_ROUTES = {
  FEEDBACK: '/support/feedback',
} as const;

// Public routes
export const PUBLIC_ROUTES = {
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
} as const;

// Helper function to create viewer route with id
export const createViewerRoute = (type: 'pdf' | 'document', juniperId: string): string => {
  return type === 'pdf'
    ? `/juniper/pdf-viewer/${juniperId}`
    : `/juniper/document-viewer/${juniperId}`;
};

/**
 * Route titles for browser tab
 * Format: {App Name} {State Code} - {Module Title}
 * Add new route titles here when creating new routes.
 */
export const ROUTE_TITLES: Record<string, string> = {
  // AR Collections
  [AR_COLLECTIONS_ROUTES.AR.COLLECTIONS]: 'Collections Queue',
  [AR_COLLECTIONS_ROUTES.AR.SETTINGS]: 'AR Settings',
  [AR_COLLECTIONS_ROUTES.AR.REPORTING]: 'AR Reporting',
  [AR_COLLECTIONS_ROUTES.AR.EMAIL_QUEUE]: 'Email Queue',
  [AR_COLLECTIONS_ROUTES.AR.APPEALS]: 'Current Appeals Queue',
  [AR_COLLECTIONS_ROUTES.AR.WRITE_OFFS]: 'Current Write-off Queue',
  [AR_COLLECTIONS_ROUTES.AR.PERFORMANCE]: 'Performance',
  [AR_COLLECTIONS_ROUTES.AR.LEDGER_REPORTS]: 'Ledger Reports',
  [AR_COLLECTIONS_ROUTES.AR.ESCALATION_REPORT]: 'Escalation Report',
  [AR_COLLECTIONS_ROUTES.PUBLIC.APPEAL_DETAIL_ATTORNEY_RESPONSE]: 'Attorney Response',

  // Reports
  [REPORTS_ROUTES.BASE]: 'Reports',
  [REPORTS_ROUTES.AR.DASHBOARD]: 'AR Report',
  [REPORTS_ROUTES.AR.HISTORY]: 'AR Report History',
  [REPORTS_ROUTES.TRUST.DASHBOARD]: 'Trust Reconciliation',
  [REPORTS_ROUTES.TRUST.HISTORY]: 'Trust History',
  [REPORTS_ROUTES.MATTER_LEDGER.DASHBOARD]: 'Matter Ledger',
  [REPORTS_ROUTES.MATTER_LEDGER.HISTORY]: 'Matter Ledger History',
  [REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD]: 'Matter Statement',
  [REPORTS_ROUTES.MATTER_STATEMENT.HISTORY]: 'Matter Statement History',

  // Settings
  [SETTINGS_ROUTES.BASE]: 'Settings',
  [SETTINGS_ROUTES.REPORTING_CONFIG]: 'Reporting Config',
  [SETTINGS_ROUTES.ARCHIVAL]: 'Archival',
  [SETTINGS_ROUTES.JUNIPER_CONFIG]: 'Juniper Config',
  [SETTINGS_ROUTES.TEMPLATE_MANAGER]: 'Template Manager',
  [SETTINGS_ROUTES.WEBHOOKS]: 'Webhooks',
  [SETTINGS_ROUTES.WEBHOOK_ACTIVITY]: 'Webhook Activity',
  [SETTINGS_ROUTES.QBO_WEBHOOKS]: 'QBO Webhooks',
  [SETTINGS_ROUTES.QBO_WEBHOOK_ACTIVITY]: 'QBO Webhook Activity',

  // Juniper
  [JUNIPER_ROUTES.TRANSACTIONS]: 'Transactions',

  // Trust Requests
  [TRUST_REQUEST_ROUTES.CREATE]: 'Trust Request Creation',

  // Support
  [SUPPORT_ROUTES.FEEDBACK]: 'Feedback',
  [ROUTES.HOME]: 'Home',
  [ROUTES.LOGIN]: 'Login',
  [ROUTES.NOT_FOUND]: 'Page Not Found',
  [ROUTES.FORBIDDEN]: 'Forbidden',
  [PUBLIC_ROUTES.PRIVACY_POLICY]: 'Privacy Policy',
  [PUBLIC_ROUTES.TERMS_OF_SERVICE]: 'Terms of Service',
  [AUTH_ROUTES.CALLBACK]: 'Authenticating...',
};
